import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Paper,
  Typography,
  MenuItem,
  Select,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import { ServiceAccessInput } from '../../organisms/settings/service-access-input';
import { Group } from '../../organisms/group';
import { useSelector } from '../../store/utils';
import { SwitchBox } from '../../molecules/switch-box';
import { ValueBox } from '../../molecules/value-box';
import { reverse } from 'lodash';
import { SimpleTable } from '../../molecules/simple-table';
import { FormattedDateTime } from '../../utils/formatted-datetime';
import { trpc } from '../../api/trpc';
import { Row } from '../../atoms/row';
import { EnergomachineBatteryMode, FlexibilityCommandType } from '../../shared';

const EnergomachineCommandTest = () => {
  const [batteryMode, setBatteryMode] = useState(
    EnergomachineBatteryMode.ManualOut
  );
  const [durationSec, setDurationSec] = useState(600);
  const [minCharge, setMinCharge] = useState(20);
  const [maxCharge, setMaxCharge] = useState(80);
  const [allowExcess, setAllowExcess] = useState(true);
  const [flowLimitKwh, setFlowLimitKwh] = useState(5);

  const { mutate } = trpc.flexibility.commandTest.useMutation();

  const submit = () => {
    mutate({
      type: FlexibilityCommandType.Energomachine,
      payload: {
        battery_mode: batteryMode,
        execution_duration_sec: durationSec,
        min_charge: minCharge,
        max_charge: maxCharge,
        allow_excess: allowExcess,
        battery_flow_limit_kwh: flowLimitKwh,
      },
    });
  };

  return (
    <Paper>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Command Test
      </Typography>

      <Row gap={1}>
        <Select
          label="Battery mode"
          value={batteryMode}
          onChange={(e) => {
            setBatteryMode(e.target.value as EnergomachineBatteryMode);
          }}
        >
          {Object.values(EnergomachineBatteryMode).map((mode) => (
            <MenuItem key={mode} value={mode}>
              {mode}
            </MenuItem>
          ))}
        </Select>

        <TextField
          value={durationSec}
          onChange={(e) => setDurationSec(Number(e.target.value))}
          label="Duration in seconds"
          size="small"
        />

        <TextField
          value={minCharge}
          onChange={(e) => setMinCharge(Number(e.target.value))}
          label="Min charge %"
          size="small"
        />

        <TextField
          value={maxCharge}
          onChange={(e) => setMaxCharge(Number(e.target.value))}
          label="Max charge %"
          size="small"
        />

        <TextField
          value={flowLimitKwh}
          onChange={(e) => setFlowLimitKwh(Number(e.target.value))}
          label="Flow limit kWh"
          size="small"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={allowExcess}
              onChange={(e) => setAllowExcess(e.target.checked)}
            />
          }
          label="Allow excess"
        />

        <Button variant="contained" size="small" onClick={submit}>
          Odeslat
        </Button>
      </Row>
    </Paper>
  );
};

const Energomachine = () => {
  const [serviceActive, connected, lastMessages, activeCommand] = useSelector(
    ({ variables }) =>
      [
        variables.SERVICE_ACTIVE,
        variables.ENERGOMACHINE_API_CONNECTED,
        variables.ENERGOMACHINE_API_LAST_MESSAGES,
        variables.FLEXIBILITY_ACTIVE_COMMAND,
      ] as const
  );

  const lastMessagesReversed = useMemo(() => {
    return reverse(lastMessages ? [...lastMessages] : []);
  }, [lastMessages]);

  if (!serviceActive) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="energomachine.title"
            defaultMessage="Energomachine"
          />
        }
      >
        <ServiceAccessInput />
      </Group>
    );
  }

  return (
    <>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="energomachine.title"
            defaultMessage="Energomachine"
          />
        }
      >
        <SwitchBox
          switchVariableId="ENERGOMACHINE_API_ENABLED"
          title={
            <FormattedMessage
              id="energomachine.enabled"
              defaultMessage="Integrace zapnuta"
            />
          }
          fullWidth
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="energomachine.connected"
              defaultMessage="Stav"
            />
          }
          value={connected ? 'Online' : 'Nepřipojeno'}
        />

        <SimpleTable
          title={
            <FormattedMessage
              id="energomachine.last_messages"
              defaultMessage="Poslední zprávy"
            />
          }
          rows={[
            [
              <FormattedMessage
                id="energomachine.last_messages.date"
                defaultMessage="Čas"
                key={0}
              />,
              <FormattedMessage
                id="energomachine.last_messages.topic"
                defaultMessage="Topic"
                key={1}
              />,
              <FormattedMessage
                id="energomachine.last_messages.payload"
                defaultMessage="Zpráva"
                key={2}
              />,
            ],
            ...lastMessagesReversed.map(({ date, payload, topic }) => {
              return [
                <FormattedDateTime value={date} key={0} />,
                JSON.stringify(topic),
                JSON.stringify(payload),
              ];
            }),
          ]}
          lastColBold={false}
          firstColBold={false}
          firstLineBold={true}
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="energomachine.business_flexibility.title"
            defaultMessage="Obchodní flexibilita"
          />
        }
      >
        <EnergomachineCommandTest />

        <Paper>{JSON.stringify(activeCommand)}</Paper>
      </Group>
    </>
  );
};

export default Energomachine;
